import type { ComponentPropsWithoutRef, ReactNode } from 'react';

export interface BadgeProps extends ComponentPropsWithoutRef<'span'> {
  variant?:
    | 'green-soft'
    | 'green-outline'
    | 'green-solid'
    | 'pending'
    | 'expired'
    | 'revoked'
    | 'neutral'
    | 'dark'
    | 'chip'
    | 'success'
    | 'warning'
    | 'danger'
    | 'teal';
  icon?: ReactNode;
  /**
   * `md` (default) = hero pill (fully rounded — signup/OTP badges);
   * `sm` = compact status/role chip (moderately rounded, NOT a pill — Figma's
   * Pending/Assigned/role chips all use a smaller radius than a true pill);
   * `count` = small counter capsule (Figma's count badge is wider than tall,
   * not a perfect circle).
   */
  size?: 'sm' | 'md' | 'count';
}

/** Named export of the variant union, so consumers can type a lookup keyed by it. */
export type BadgeVariant = NonNullable<BadgeProps['variant']>;

// Radius lives here (per size), not in the shared base class, because `sm`
// chips are deliberately LESS rounded than the `md` hero pill — a single
// hardcoded `rounded-full` on every variant was the bug that made every small
// chip render as a full pill when Figma shows a moderate radius instead.
// Guideline §9 (Badges & Pills): "Standard badge" (sm, the moderate-radius
// chip) is 10-11px; "Rounded pill" (md/count, fully rounded) is 11-12px —
// both use 11px so they satisfy either end of their own range.
const SIZE_CLASSES: Record<NonNullable<BadgeProps['size']>, string> = {
  sm: 'rounded-lg px-2.5 py-0.5 text-[10px]',
  md: 'rounded-full px-4 py-1.5 text-[11px]',
  // Capsule for a single-digit counter: min-width (not a fixed square) so it
  // stays a pill-ish oval like Figma's count badge, never a forced circle.
  count: 'rounded-full h-5 min-w-6 px-1.5 justify-center text-[11px]',
};

// The three neutral-ish variants below carry an `admin-dark:` override because
// their light styling is a TRANSLUCENT brand colour (`bg-navy/5`, `bg-slate/10`)
// or solid navy, none of which restates as a semantic token without changing
// what member screens render. Every other variant is semantic (success, warning,
// danger, the invite statuses, green-*) and is deliberately left alone: those
// colours carry meaning and must look the same in both themes.
const VARIANT_CLASSES: Record<NonNullable<BadgeProps['variant']>, string> = {
  // Used by ProfileHeaderCard's "Private" badge (investor module) - keep this
  // exact value, don't merge it with the differently-styled `chip`/`revoked`
  // greys below.
  neutral: 'bg-navy/5 text-navy admin-dark:bg-surface-raised admin-dark:text-text-secondary',
  // Solid backgrounds per ui-guidelines.md §9 (Badges & Pills) — no soft/tinted
  // `/10` fills for the semantic status variants.
  success: 'bg-green text-white',
  // Same semantic slot as `pending` below (Warning/Pending, §9) — kept as a
  // separate variant name since other call sites depend on it, but styled
  // identically: solid yellow, navy text.
  warning: 'bg-yellow text-navy',
  danger: 'bg-red text-white',
  // "Live" status (Trusted Partner offers, MYN-104) - solid `teal2` (#2B958E),
  // NOT the `teal` token (#00405C, a distinct dark navy-teal already used
  // elsewhere) - extracted directly from Figma node 322:6281.
  teal: 'bg-teal2 text-white',
  'green-soft': 'bg-green/10 text-dkgreen',
  'green-outline': 'border border-green/40 bg-white text-dkgreen',
  // Solid brand green + white — the count badge and the Active/Assigned status
  // chips (all render solid green in the Figma teams-and-permissions design).
  'green-solid': 'bg-green text-white',
  // Pending invite chip — solid yellow with navy text (Figma).
  pending: 'bg-yellow text-navy',
  expired: 'bg-danger/10 text-danger',
  revoked: 'bg-slate/10 text-slate',
  // Dark navy chip — the current user's role marker ("Startup Admin · You")
  // and the DAXE-seats usage pill on the Team Access page.
  dark: 'bg-navy text-white admin-dark:bg-surface-raised admin-dark:text-text-primary',
  // Pale grey chip with navy text — a non-self member's role label
  // ("Startup Member", "Startup Admin") in the Team Members table.
  chip: 'bg-slate/10 text-navy admin-dark:bg-surface-raised admin-dark:text-text-secondary',
};

/**
 * Pill badge. Semantic variants (`neutral`/`success`/`warning`/`danger`) for
 * status pills and role badges; `green-soft`/`green-outline` for hero headers
 * ("Welcome to My NEXT Raise", "Security Check") and status chips ("Sent").
 */
export function Badge({
  variant = 'green-soft',
  size = 'md',
  icon,
  className = '',
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-sans font-semibold ${SIZE_CLASSES[size]} ${VARIANT_CLASSES[variant]} ${className}`.trim()}
      {...props}
    >
      {icon && <span aria-hidden="true">{icon}</span>}
      {children}
    </span>
  );
}
